// frontend/src/utils/lazyWithRetry.tsx
/**
 * Lazy loading com retry automático e fallback
 * Solução definitiva para ChunkLoadError em produção
 */

import { ComponentType, lazy, LazyExoticComponent } from 'react';

interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Cria um componente lazy com retry automático
 * Se falhar após todas as tentativas, recarrega a página
 *
 * Nota: Usa 'any' para compatibilidade com React.lazy padrão
 * O tipo é inferido corretamente no uso
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): LazyExoticComponent<T> {
  const { maxRetries = 3, retryDelay = 1000 } = options;

  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      let retryCount = 0;

      const attemptLoad = () => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            // Verificar se é ChunkLoadError
            const isChunkError =
              error?.name === 'ChunkLoadError' ||
              error?.message?.includes('Loading chunk') ||
              error?.message?.includes('ChunkLoadError');

            if (isChunkError && retryCount < maxRetries) {
              retryCount++;
              console.warn(
                `[LazyRetry] Chunk load failed, retry ${retryCount}/${maxRetries}...`,
                error
              );

              // Aguardar antes de tentar novamente
              setTimeout(attemptLoad, retryDelay * retryCount);
            } else if (isChunkError && retryCount >= maxRetries) {
              // Última tentativa: recarregar a página
              console.error(
                '[LazyRetry] Max retries reached, reloading page...',
                error
              );
              window.location.reload();
            } else {
              // Outro tipo de erro, rejeitar normalmente
              reject(error);
            }
          });
      };

      attemptLoad();
    });
  });
}

/**
 * Versão com cache busting - adiciona timestamp na URL
 *
 * Nota: Usa 'any' para compatibilidade com React.lazy padrão
 * O tipo é inferido corretamente no uso
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithCacheBust<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => {
    // Adicionar timestamp para forçar reload
    const timestamp = new Date().getTime();

    return importFunc().catch((error) => {
      console.warn('[LazyCache] Failed to load, busting cache...', error);

      // Forçar reload da página com cache bust
      window.location.href = `${window.location.pathname}?t=${timestamp}`;

      // Retornar promessa pendente para evitar erro
      return new Promise(() => {});
    });
  });
}
